import React, { useState } from 'react';

import { ApplicationRoutes, FRAGNET_SEARCH_ROLE } from 'common/constants';
import AdapterLink from 'components/AdapterLink';
import DocsLinks from 'components/Docs/DocsLinks';
import LoginView from 'components/Login';
import YouTube from 'react-youtube';

import {
  Container,
  Grid,
  Link,
  Paper,
  Step,
  StepButton,
  Stepper,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useKeycloak } from '@react-keycloak/web';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: '2rem',
      margin: '1rem',
      textAlign: 'left',
    },
    stepperContent: {
      margin: '0 auto',
      height: '5rem',
    },
    box: {
      backgroundColor: 'rgba(255,229,100,0.2)',
      borderLeft: '5px solid #ffe564',
      padding: '1rem',
      maxWidth: '60%',
      margin: '0 auto',
    },
    videos: {
      marginBottom: theme.spacing(2),
    },
  }),
);

const HomeView = () => {
  const classes = useStyles();

  const { keycloak, initialized } = useKeycloak();
  const isAuthenticated = keycloak?.authenticated;

  const roles = keycloak?.tokenParsed?.realm_access?.roles;

  const email = `info@informaticsmatters.com`;
  const steps = [
    isAuthenticated ? `You're already logged in` : 'Log in. ',
    'On the query tab, specify a query and execute. You will be taken to the Results tab.',
    'Explore molecules from the different groups and choose which one to add to you shopping cart.',
    'On the cart tab, fetch the availability of your chosen molecules.',
    'Switch to a supplier and take whatever actions are needed to purchase the molecules. Details differ between suppliers.',
  ];
  const [activeStep, setActiveStep] = useState(0);
  if (isAuthenticated) {
    activeStep < 1 && setActiveStep(1);
  }

  const handleStepClick = (index: number) => {
    setActiveStep(index);
  };

  const content = [
    <LoginView />,
    <>
      {isAuthenticated &&
        (roles?.includes(FRAGNET_SEARCH_ROLE) ? (
          <>
            <Link component={AdapterLink} to={ApplicationRoutes.Query}>
              Go to the Query tab.
            </Link>
            <Typography>Use the editor or load an example.</Typography>
          </>
        ) : (
          <Typography>
            <b>You do not have the correct access role for Fragnet Search</b>
          </Typography>
        ))}
    </>,
    <>
      Find the different groups in the sidebar then add desired molecules to your cart.{' '}
      {isAuthenticated && (
        <Link component={AdapterLink} to={ApplicationRoutes.Results}>
          Go to the Results tab
        </Link>
      )}
    </>,
    <>
      Choose the forms you want from the different suppliers.{' '}
      {isAuthenticated && (
        <Link component={AdapterLink} to={ApplicationRoutes.Carts}>
          Go to the Cart tab
        </Link>
      )}
    </>,
  ];

  return (
    <Container>
      <Paper className={classes.root}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          Welcome to Fragnet Search.
        </Typography>
        {/* TODO: If not logged in stuff */}
        <div className={classes.box}>
          <Typography variant="subtitle2" align="center">
            Fragnet Search is currently in evaluation phase. In order to obtain a login contact{' '}
            <Link href={`mailto:${email}`}>{email}</Link>.
          </Typography>
          <Typography component="p" align="center">
            Once the evaluation phase is over you will need a subscription to access the
            application.
          </Typography>
        </div>

        <Typography variant="h5" component="h2" gutterBottom>
          Quick Start
        </Typography>
        <Stepper activeStep={activeStep} nonLinear alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepButton onClick={() => handleStepClick(index)}>{label}</StepButton>
            </Step>
          ))}
        </Stepper>
        {activeStep < --steps.length && (
          <div className={classes.stepperContent}>
            <Typography align="center" paragraph>
              {content[activeStep]}
            </Typography>
          </div>
        )}

        <Grid container spacing={2} className={classes.videos}>
          <Grid item sm={4} md={4} lg={4}>
            <Typography component="p" variant="body2">
              See Fragnet Search in action with our Quick Start YouTube video
            </Typography>
          </Grid>
          <Grid item sm={4} md={4} lg={4}>
            <Typography component="p" variant="body2">
              or our Joint Webinar with ChemSpace .
            </Typography>
          </Grid>
          <Grid item sm={4} md={4} lg={4}>
            <Typography component="p" variant="body2">
              or the presentation for Edelweiss Connect.
            </Typography>
          </Grid>
          {process.env.NODE_ENV === 'production' && (
            <>
              <Grid item sm={4} md={4} lg={4}>
                <YouTube videoId={'VEd5ulEE8DY'} opts={{ width: '100%', height: 225 }} />
              </Grid>
              <Grid item sm={4} md={4} lg={4}>
                <YouTube videoId={'EJ1KyiD-CVA'} opts={{ width: '100%', height: 225 }} />
              </Grid>
              <Grid item sm={4} md={4} lg={4}>
                <YouTube videoId={'kSgSTzdYArs'} opts={{ width: '100%', height: 225 }} />
              </Grid>
            </>
          )}
        </Grid>

        <Typography variant="h5" component="h2" gutterBottom>
          Full documentation
        </Typography>

        <DocsLinks />

        <Typography variant="h5">Background</Typography>

        <Typography component="p" paragraph variant="body2">
          Fragnet search is a set of tools inspired by the work done at{' '}
          <Link target="_blank" rel="noopener noreferrer" href="https://astx.com/">
            Astex Pharmaceuticals
          </Link>{' '}
          and described in this publication{' '}
          <cite>
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href="https://pubs.acs.org/doi/10.1021/acs.jmedchem.7b00809"
            >
              <i>doi:10.1021/acs.jmedchem.7b00809</i>
            </Link>
          </cite>
          . It describes methodology for creating a network of molecules based on their
          fragmentation and how Astex use this for selecting compounds for fragment based drug
          discovery. To understand how best to use Fragnet Search it is strongly recommended to read
          this paper.
        </Typography>
        <Typography component="p" paragraph variant="body2">
          The Astex paper describes the methodology very nicely, but the code was not released and
          being based on the Daylight toolkit is not a viable option today. The{' '}
          <Link
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.diamond.ac.uk/Instruments/Mx/Fragment-Screening.html"
          >
            XChem project
          </Link>{' '}
          at the Diamond Light Source (Harwell, UK) saw the utility of the fragment network in their
          X-ray crystallograpy based fragment screening program, and Anthony Bradley (then at
          Diamond) re-implemented the fragment network methodology as a set of Python modules using
          the open source RDKit cheminformatics toolkit. This code can be found{' '}
          <Link
            target="_blank"
            rel="noopener noreferrer"
            href="https://github.com/xchem/fragalysis"
          >
            here
          </Link>
          . This is used in their{' '}
          <Link target="_blank" rel="noopener noreferrer" href="https://fragalysis.diamond.ac.uk/">
            Fragalysis
          </Link>{' '}
          application that forms the heart of their fragment screening follow up work.
        </Typography>
        <Typography component="p" paragraph variant="body2">
          We at Informatics Matters have been helping Diamond with this work for some time, and are
          now making sure these novel methodologies are available outside the context of fragment
          screening at Diamond. We believe it has a number of uses, but the prime ones are use for
          fragment screening and chemical building block selection. Fragnet Search is our current
          work in this area.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom>
          About Fragnet Search
        </Typography>
        <Typography variant="body2" paragraph>
          The purpose of the Fragnet Search application is to provide a simple to use browser based
          tool that allows you to search and explore the fragment network for compounds from a
          number of chemical suppliers. Fragnet Search utilises information provided by suppliers
          and let's you link back to those suppliers to purchase the compounds. We aim to be
          supplier neutral and welcome addition of new suppliers.
        </Typography>
        <Typography component="p" paragraph variant="body2">
          The Fragnet Search application is the endpoint of a quite complex path starting with
          building the fragment network from compound availability information provided by the
          suppliers, combining this information from multiple suppliers into a{' '}
          <Link target="_blank" rel="noopener noreferrer" href="https://neo4j.com/">
            Neo4j
          </Link>{' '}
          graph database. That database can be searched using a REST API. Fragnet Search uses that
          REST API to allow queries to be executed and results to be viewed and explored.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom>
          Access to Fragnet Search
        </Typography>
        <Typography component="p" paragraph variant="body2">
          Once the evaluation phase is complete you will need a subscription to access Fragnet
          Search and its search API. We also aim to support companies wanting their own deployment,
          containing their own proprietary compound data. Contact us if this is of interest.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom>
          Current datasets
        </Typography>
        <Typography component="p" paragraph variant="body2">
          The datasets currently available in Fragnet Search are:
        </Typography>
        <ul>
          <Typography component="li" variant="body2">
            Molport (July 2019 data)
          </Typography>
          <Typography component="li" variant="body2">
            ChemSpace Building Blocks (July 2019 data)
          </Typography>
        </ul>

        <Typography variant="h5" component="h2" gutterBottom>
          Other ways of accessing the fragment network
        </Typography>
        <ul>
          <Typography component="li" variant="body2">
            The REST API can be used outside of Fragnet Search. One approach is to use is directly
            from your programs (one user did this using Pipeline Pilot).
          </Typography>
          <Typography component="li" variant="body2">
            We also provide a Python Scripting interface for Optibrium's Stardrop software.
          </Typography>
          <Typography component="li" variant="body2">
            Both of these mechanisms will be possible from a single subscription. Choose whichever
            mode of access is best for you.
          </Typography>
          <Typography component="li" variant="body2">
            If you are involved with fragment screening at Diamond you should look at their{' '}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href="https://fragalysis.diamond.ac.uk/"
            >
              Fragalysis
            </Link>{' '}
            application which is targeted at this more specific need.
          </Typography>
        </ul>
        <Typography variant="h5" component="h2" gutterBottom>
          Additional information
        </Typography>

        <Typography variant="body2">
          Please contact{' '}
          <Link target="_blank" rel="noopener noreferrer" href={`mailto:${email}`}>
            {email}
          </Link>{' '}
          for more information about Fragnet Search and related services.
        </Typography>
      </Paper>
    </Container>
  );
};

export default HomeView;
